<script lang="ts">
	import { format, locale } from 'svelte-i18n';
	// Svelte functions
	import { createEventDispatcher } from 'svelte';
	// Types
	import type { Device } from 'src/types/Device';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	// Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { toast } from '@Shelly/shelly-design-system';
	// Components
	import VideoActions from '../CameraPlayer/VideoActions.svelte';
	import VideoActionButton from '../CameraPlayer/VideoActionButton.svelte';
	// Helpers
	import {
		getCameraComponentKey,
		getManualRecording,
		isAnyStorageAvailable,
		isCameraArmed,
		isCameraPrivacyEnabled,
	} from '../helpers/camera-helpers';
	import { buildCameraArmPrivacyCall } from '../helpers/camera-features';
	import { hasCameraCloudStorageCached } from 'src/helpers/camera_cloud_storage_helpers';
	import type { CameraRecordings } from '../types/camera-status';
	import { PttUiState } from '../types/ptt-ui-state';

	/** The arm/disarm toast carries two sentences, so the 4s default is too short to read. */
	const MOTION_TOAST_DURATION = 8000;

	const dispatch = createEventDispatcher<{ talkToggle: boolean }>();

	export let device: Device;
	export let status: DeviceStatusG2;
	export let settings: DeviceSettingsG2;
	export let pttState: PttUiState = PttUiState.UNAVAILABLE;
	export let streamLoading: boolean = false;

	// State variables
	let isRecordLoading = false;
	let isPhotoLoading = false;
	let photoSaved = false;
	let isArmLoading = false;
	let isPrivacyLoading = false;

	// Reactive values from device status/settings
	$: channel = device?.channel || 0;
	$: cameraKey = getCameraComponentKey(device);
	$: isArmed = isCameraArmed(device, status, settings);
	$: isPrivacyMode = isCameraPrivacyEnabled(device, status, settings);
	$: privacyLabel =
		$locale === 'en'
			? $format('camera_action_privacy', { default: 'Privacy' })
			: isPrivacyMode
				? $format('camera_action_show', { default: 'Show' })
				: $format('camera_action_privacy', { default: 'Privacy' });
	// Talkback usability is driven by the PTT channel state itself, not a
	// stream-liveness proxy: `unavailable` (no channel yet) and `closed`
	// (channel dropped) are the only blocking states.
	$: isTalkDisabled =
		streamLoading || pttState === PttUiState.UNAVAILABLE || pttState === PttUiState.CLOSED;
	$: recordings = status?.[cameraKey]?.recordings as CameraRecordings | undefined;
	$: activeRecording = getManualRecording(recordings);
	$: isRecording = !!activeRecording;
	// Snapshots and recordings land in the owner's cloud storage, encrypted with
	// their password, so a guest of a shared camera cannot create them. Without an
	// active card or a cloud slot there is nowhere to put them at all.
	$: isCaptureDisabled = isPrivacyMode || device?.shared || !isAnyStorageAvailable(device, status);

	const handlePhoto = async () => {
		isPhotoLoading = true;
		const res = await gen2Request(device, 'Camera.CaptureImage', { id: channel, stream: 0 });
		isPhotoLoading = false;
		if (res?.isok) {
			photoSaved = true;
			// Reset after showing success
			await new Promise((resolve) => setTimeout(resolve, 2000));
			photoSaved = false;
		} else {
			toast.open({
				type: 'danger',
				title: $format('camera_capture_image_error', {
					default: 'Failed to capture image',
				}),
			});
		}
	};

	const handleRecord = async () => {
		isRecordLoading = true;
		if (isRecording && activeRecording?.id) {
			const res = await gen2Request(device, 'Camera.StopRecording', {
				id: channel,
				rec_id: activeRecording.id,
			});
			if (!res?.isok) {
				toast.open({
					type: 'danger',
					title: $format('camera_stop_recording_error', {
						default: 'Failed to stop recording',
					}),
				});
			}
		} else {
			const res = await gen2Request(device, 'Camera.StartRecording', { id: channel });
			if (!res?.isok) {
				toast.open({
					type: 'danger',
					title: $format('camera_start_recording_error', {
						default: 'Failed to start recording',
					}),
				});
			}
		}
		isRecordLoading = false;
	};

	/**
	 * Apply `arm` and/or `privacy`. The device reports the new value back, so the buttons
	 * follow the device rather than a locally written one.
	 */
	const applyCameraFlags = async (patch: { arm?: boolean; privacy?: boolean }) => {
		const { method, params } = buildCameraArmPrivacyCall(device, patch, status);
		const res = await gen2Request(device, method, params);
		return !!res?.isok;
	};

	/**
	 * Explain what arming/disarming does to the phone notifications, since the button
	 * itself gives no hint that this is what `arm` drives.
	 */
	const showMotionNotificationToast = (armed: boolean) => {
		if (armed) {
			// Without a cloud slot the notification carries no snapshot, so the copy sells
			// Cloud Storage instead of promising one.
			toast.open({
				type: 'success',
				title: $format('camera_arm_toast_title', { default: 'Motion notifications enabled' }),
				text: hasCameraCloudStorageCached(device?.id ?? '')
					? $format('camera_arm_toast_text_cloud', {
							default:
								"You'll get a notification with a snapshot on your phone whenever motion starts.",
						})
					: $format('camera_arm_toast_text_no_cloud', {
							default:
								"You'll get a notification on your phone whenever motion starts. Get Cloud Storage to continue seeing snapshots in the notification.",
						}),
				duration: MOTION_TOAST_DURATION,
			});
			return;
		}

		const body = $format('camera_disarm_toast_text', {
			default: "We won't send phone notifications when this camera detects motion.",
		});
		const storageNote = $format('camera_disarm_toast_text_storage', {
			default: 'Motion recordings will continue as usual, so you can still review what happened.',
		});
		toast.open({
			type: 'success',
			title: $format('camera_disarm_toast_title', { default: 'Motion notifications disabled' }),
			text: isAnyStorageAvailable(device, status) ? `${body} ${storageNote}` : body,
			duration: MOTION_TOAST_DURATION,
		});
	};

	const handleArm = async () => {
		const nextArmed = !isArmed;
		isArmLoading = true;
		const applied = await applyCameraFlags({ arm: nextArmed });
		isArmLoading = false;
		if (!applied) {
			toast.open({
				type: 'danger',
				title: isArmed
					? $format('camera_disarm_error', { default: 'Failed to disarm' })
					: $format('camera_arm_error', { default: 'Failed to arm' }),
			});
			return;
		}
		showMotionNotificationToast(nextArmed);
	};

	const handleTalkToggle = () => {
		if (pttState === PttUiState.REQUESTING || isTalkDisabled) return;
		// Click only emits intent; the parent decides when to flip `pttState`.
		dispatch('talkToggle', pttState !== PttUiState.GRANTED);
	};

	const handlePrivacy = async () => {
		isPrivacyLoading = true;
		const applied = await applyCameraFlags({ privacy: !isPrivacyMode });
		isPrivacyLoading = false;
		if (!applied) {
			toast.open({
				type: 'danger',
				title: isPrivacyMode
					? $format('camera_disable_privacy_error', {
							default: 'Failed to disable privacy mode',
						})
					: $format('camera_enable_privacy_error', {
							default: 'Failed to enable privacy mode',
						}),
			});
		}
	};
</script>

<VideoActions>
	<VideoActionButton
		icon={photoSaved ? 'fas fa-circle-check' : 'fas fa-camera'}
		label={photoSaved
			? $format('status_saved', { default: 'Saved' })
			: $format('camera_action_photo', { default: 'Photo' })}
		loading={isPhotoLoading}
		disabled={isCaptureDisabled}
		variant={photoSaved ? 'success' : 'default'}
		on:click={handlePhoto}
	/>
	<VideoActionButton
		icon={isRecording ? 'fas fa-circle-stop' : 'fas fa-circle-dot'}
		label={isRecording
			? $format('label_stop', { default: 'Stop' })
			: $format('camera_action_record', { default: 'Record' })}
		loading={isRecordLoading}
		disabled={isCaptureDisabled}
		variant={isRecording ? 'danger' : 'default'}
		on:click={handleRecord}
	/>
	<VideoActionButton
		icon="fas fa-microphone"
		label={$format('camera_action_talk', { default: 'Talk' })}
		active={pttState === PttUiState.GRANTED}
		loading={pttState === PttUiState.REQUESTING}
		disabled={isTalkDisabled}
		variant={pttState === PttUiState.GRANTED ? 'warning' : 'default'}
		on:click={handleTalkToggle}
	/>
	<VideoActionButton
		icon={isArmed ? 'fas fa-shield' : 'fas fa-shield-halved'}
		label={isArmed
			? $format('label_action_alarm_disarm', { default: 'Disarm' })
			: $format('label_action_alarm_arm', { default: 'Arm' })}
		loading={isArmLoading}
		disabled={isPrivacyMode}
		variant={isArmed ? 'danger' : 'default'}
		on:click={handleArm}
	/>
	<VideoActionButton
		icon={isPrivacyMode ? 'fas fa-video' : 'fas fa-video-slash'}
		label={privacyLabel}
		loading={isPrivacyLoading}
		variant={isPrivacyMode ? 'warning' : 'default'}
		on:click={handlePrivacy}
	/>
</VideoActions>
